// src/pages/AlwaysConnected.jsx

import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export default function AlwaysConnected() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">

      <Helmet>

        <title>Always Connected | Managed SIM Connectivity | SeguraTech</title>

        <meta
          name="description"
          content="Keep your Ajax alarm system connected with SeguraTech's professionally managed SIM service. Unlimited data, activated and managed for just £60 per year."
        />

        <meta
          property="og:title"
          content="Always Connected | SeguraTech"
        />

        <meta
          property="og:description"
          content="Professionally managed mobile connectivity for Ajax alarm systems."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.seguratech.co.uk/alarms/always-connected"
        />

        <meta property="og:image" content="/logo152.png" />

      </Helmet>

      {/* Hero */}

      <section className="max-w-5xl mx-auto px-6 pt-24 pb-20 text-center">

        <h1 className="text-4xl md:text-6xl font-extrabold text-[#C6A564] mb-8">
          Always Connected
        </h1>

        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-8">
          Keep your Ajax alarm system connected with a professionally managed
          mobile SIM, providing an additional communication path for alarm
          notifications and professional monitoring.
        </p>

      </section>

      {/* Main Content */}

      <section className="max-w-5xl mx-auto px-6 pb-24 space-y-20">

        {/* Why */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Why Does It Matter?
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Most alarm systems rely on your home internet connection to send
              notifications and communicate with monitoring services.
            </p>

            <p>
              While broadband is generally very reliable, no connection is
              immune to faults, outages or unexpected interruptions.
            </p>

            <p>
              By adding a professionally managed mobile SIM, your Ajax system
              has an additional way to communicate, helping keep your security
              system connected when it matters most.
            </p>

          </div>

        </div>

        {/* What's Included */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            What's Included?
          </h2>

          <div className="space-y-4 text-white/80">

            <p>Every subscription includes:</p>

            <ul className="space-y-3 ml-6 list-disc">

              <li>Professionally activated SIM card.</li>

              <li>Unlimited mobile data.</li>

              <li>Installed and configured for your Ajax system.</li>

              <li>Fully managed by SeguraTech.</li>

              <li>Suitable for alarm notifications.</li>

              <li>Compatible with professional monitoring.</li>

            </ul>

          </div>

        </div>

        {/* Monitoring */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Designed for Peace of Mind
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Whether your system simply sends notifications to your phone or
              is professionally monitored through{" "}
              <a
                href="https://www.arcmon.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C6A564] hover:underline"
              >
                Arc Monitoring
              </a>
              , mobile connectivity provides an additional communication path
              should your primary internet connection become unavailable.
            </p>

            <p>
              Everything is supplied, activated and managed by SeguraTech,
              meaning there's no PAYG credit to top up, no separate mobile
              account to manage and no complicated setup.
            </p>

          </div>

        </div>

        {/* Pricing */}

        <div className="border border-[#C6A564] rounded-2xl bg-[#1a1a1a] p-12 text-center">

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Simple Annual Pricing
          </h2>

          <p className="text-5xl font-bold text-[#C6A564] mb-8">
            From £60 per year 
          </p>

          <p className="text-white/80 leading-8 max-w-2xl mx-auto">
            One simple annual subscription providing a professionally managed,
            activated SIM with unlimited mobile data for your Ajax alarm
            system.
          </p>

        </div>

        {/* CTA */}

        <div className="border border-[#C6A564] rounded-2xl bg-[#1a1a1a] p-12 text-center">

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Stay Connected
          </h2>

          <p className="text-white/80 leading-8 max-w-3xl mx-auto">
            Whether you're installing a new Ajax system or upgrading an
            existing installation, we can provide professionally managed
            mobile connectivity to help keep your alarm connected.
          </p>

          <Link
            to="/contact"
            className="inline-block mt-10 bg-[#C6A564] text-[#111111] font-semibold px-10 py-4 rounded-xl hover:opacity-90 transition"
          >
            Contact SeguraTech
          </Link>

        </div>

      </section>

    </div>
  );
}